import { useState } from 'react';
import { Card, Form, Button, InputGroup } from 'react-bootstrap';
import LoadingSpinner from '../common/LoadingSpinner';
import { customerContactService } from '../../services/customerContactService';
import { NOTE_TYPES, NOTE_TYPE_VALUES } from '../../constants/constants';
import { availableContactFundTypes } from '../../utils/fundTypeUtils';
import Required from '../../utils/Required';
import { useUI } from '../../context/UIContext';

const CustomerContactSection = ({ state }) => {

  const { showError, showSuccess, hideAlert } = useUI();
  
  const [fundType, setFundType] = useState('');
  const [noteType, setNoteType] = useState('');
  const [appointmentDate, setAppointmentDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [validated, setValidated] = useState(false);

  const fundTypes = availableContactFundTypes(state);

  const handleSubmit = async (e) => {
    e.preventDefault();
    hideAlert();

    const form = e.currentTarget;

    if (!form.checkValidity()) {
      setValidated(true);
      return;
    }

    setLoading(true);
    try {
      let formattedDate = null;
      if (noteType === NOTE_TYPE_VALUES.APPOINTMENT_DATE && appointmentDate) {
        const [year, month, day] = appointmentDate.split('-');
        formattedDate = `${month}/${day}/${year}`;
      }

      const response = await customerContactService.createCustomerContact(
        fundType,
        noteType,
        formattedDate
      );

      if (response.success) {
        showSuccess(response.message || 'Customer Contact Created Successfully');
        setFundType('');
        setNoteType('');
        setAppointmentDate('');
        setValidated(false);
      } else {
        showError(response.error || 'Failed to create customer contact.');
      }
    } catch (err) {
      console.error('Error creating customer contact:', err);
      showError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <Card className="shadow mb-4">
      <Card.Header>
        <h5 className="mb-0">Customer Contact</h5>
      </Card.Header>
      <Card.Body>
        <Form  noValidate validated={validated} onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <InputGroup>
              <Form.FloatingLabel label={<Required>Fund Type</Required>} controlId="fund-type">
                <Form.Select
                  value={fundType}
                  onChange={(e) => setFundType(e.target.value)}
                  disabled={loading}
                  required
                >
                  <option value="">Select Fund Type</option>
                  {fundTypes && fundTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  Please select a Fund Type.
                </Form.Control.Feedback>
              </Form.FloatingLabel>
            </InputGroup>
          </Form.Group>

          <Form.Group className="mb-3">
            <InputGroup>
              <Form.FloatingLabel label={<Required>Note Type</Required>} controlId="note-type">
                <Form.Select
                  value={noteType}
                  onChange={(e) => setNoteType(e.target.value)}
                  disabled={loading}
                  required
                >
                  <option value="">Select Note Type</option>
                  {NOTE_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  Please select a Note Type.
                </Form.Control.Feedback>
              </Form.FloatingLabel>
            </InputGroup>
          </Form.Group>

          {noteType === NOTE_TYPE_VALUES.APPOINTMENT_DATE && (
            <Form.Group className="mb-3">
              <InputGroup>
                <Form.FloatingLabel label={<Required>Appointment Date</Required>} controlId="appointment-date">
                  <Form.Control
                    type="date"
                    value={appointmentDate}
                    onChange={(e) => setAppointmentDate(e.target.value)}
                    min={today}
                    disabled={loading}
                    required
                  />
                  <Form.Text muted>Select the confirmed appointment date.</Form.Text>
                </Form.FloatingLabel>
              </InputGroup>
            </Form.Group>
          )}

          <Button variant="primary" type="submit" className="w-100" disabled={loading}>
            {loading ? (
              <LoadingSpinner inline message="Submitting..." />
            ) : (
              'Submit Customer Contact'
            )}
          </Button>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default CustomerContactSection;
